import { InputSearchGroup } from '@/src/component/superstack/base/InputSearchGroup';
import { Input } from '@/src/component/superstack/base/Inputs';
import Check from '@/asset/superstack/svgs/check.svg';
import { useState, useRef, useEffect } from 'react';
import classNames from 'classnames/bind';
import styles from '@/styles/superstack/stacks/search.module.scss';
import { SideBarIndexScroll } from '@/src/component/superstack/base/SideBarIndex';
import { useOutsideClick } from '@/src/utils/hooks/useOutsideClick';
import { asciiAlphaUpper } from '@/src/utils/consts';

const stackYearMin = 0;
const stackYearMax = 15;

const cx = classNames.bind(styles);

const StackSearchBar = ({ staticStacks, stacks, setStacks, withRecoil = false, sx = {} }) => {
  const [query, setQuery] = useState('');
  const [hasFocused, setHasFocused] = useState(false);
  const [highlightedStackIdx, setHighlightedStackIdx] = useState(-1);
  const elementListRef = useRef([]);

  const searchedStacks = staticStacks.filter((staticStack) =>
    staticStack.name.toLowerCase().includes(query.toLowerCase()),
  );

  useEffect(() => {
    if (query.length > 0) {
      setHasFocused(() => {
        return true;
      });
    }
  }, [query]);

  useEffect(() => {
    setHighlightedStackIdx(-1);
  }, [searchedStacks.length]);

  const canShowResult = () => {
    return staticStacks && query && hasFocused;
  };

  const StackSearchBarRef = useOutsideClick(() => {
    setHasFocused(() => {
      return false;
    });
  });

  const stackFlags = () => {
    if (withRecoil) {
      return Object.keys(stacks).map((flag) => {
        return flag;
      });
    } else {
      return stacks.map((stack) => {
        return stack.flag;
      });
    }
  };

  const isSelected = (staticStack) => {
    return stackFlags().includes(staticStack.flag);
  };

  const handleStackSelection = (searchedStack) => {
    withRecoil
      ? handleRecoilStackClick(searchedStack)
      : handleStackClick(isSelected(searchedStack), searchedStack);
  };

  const handleStackClick = (isSelected, staticStack) => {
    setStacks((selectedStacks) => {
      if (!isSelected) {
        return selectedStacks.concat([
          {
            flag: staticStack.flag,
            name: staticStack.name,
            requiredYear: 0,
          },
        ]);
      } else {
        setStacks(
          selectedStacks.filter((selected) => {
            return selected.flag !== staticStack.flag;
          }),
        );
      }
    });
  };

  const handleRecoilStackClick = (staticStack) => {
    if (!Object.prototype.hasOwnProperty.call(stacks, staticStack.flag)) {
      const newStacks = { ...stacks };
      newStacks[staticStack.flag] = [stackYearMin, stackYearMax];
      setStacks(newStacks);
    } else {
      const newStacks = { ...stacks };
      delete newStacks[staticStack.flag];
      setStacks(newStacks);
    }
  };

  const handleInputKeyDown = (e) => {
    if (!hasFocused) {
      return false;
    }
    if (e.keyCode === 38) {
      // Arrow Up
      e.preventDefault();
      if (highlightedStackIdx === 0) {
        setHighlightedStackIdx(searchedStacks.length - 1);
      } else {
        setHighlightedStackIdx((highlightedStackIdx - 1) % searchedStacks.length);
        elementListRef.current[highlightedStackIdx - 1]?.scrollIntoView({ block: 'nearest' });
      }
    } else if (e.keyCode === 40) {
      // Arrow Down
      e.preventDefault();
      setHighlightedStackIdx((highlightedStackIdx + 1) % searchedStacks.length);
      elementListRef.current[highlightedStackIdx + 1]?.scrollIntoView({ block: 'nearest' });
    } else if (e.keyCode === 13) {
      // Enter
      e.preventDefault();
      handleStackSelection(searchedStacks[highlightedStackIdx]);
    } else if (e.keyCode === 27) {
      // ESC
      setHasFocused(() => {
        return false;
      });
    }
  };

  return (
    <section className={cx(['content', 'search'])} style={sx} ref={StackSearchBarRef}>
      <InputSearchGroup size={'lg'}>
        <Input
          size={'sm'}
          placeholder="검색할 기술 스택을 입력해 주세요."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          onFocus={() => {
            setHasFocused(() => {
              return true;
            });
          }}
          onKeyDown={handleInputKeyDown}
        />
      </InputSearchGroup>
      {canShowResult() && (
        <div className={cx('search-list-wrapper')}>
          <ul className={cx('content-body-ul')}>
            {searchedStacks.map((searchedStack, index) => {
              return (
                <li
                  key={searchedStack.flag}
                  className={cx([
                    'content-body-li',
                    index === highlightedStackIdx ? 'highlighted' : '',
                  ])}
                  onClick={() => {
                    handleStackSelection(searchedStack);
                  }}
                  onMouseOver={() => setHighlightedStackIdx(index)}
                >
                  {isSelected(searchedStack) && <Check width={16} height={16} fill={'#34C759'} />}
                  <span ref={(el) => (elementListRef.current[index] = el)}>
                    {searchedStack.name}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
};

const StackFilterBox = ({
  staticStacks,
  stacks,
  setStacks,
  scrollable = false,
  scrollHeight = null,
  withIndexScroll = false,
  withRecoil = false,
  sx = {},
}) => {
  const [query, setQuery] = useState('');
  const [highlightedStackIdx, setHighlightedStackIdx] = useState(-1);
  const elementListRef = useRef([]);

  const searchedStacks = staticStacks.filter((staticStack) =>
    staticStack.name.toLowerCase().includes(query.toLowerCase()),
  );

  useEffect(() => {
    setHighlightedStackIdx(-1);
  }, [searchedStacks.length]);

  const indexIdList = asciiAlphaUpper.map((x) => String.fromCharCode(x));
  let curIndex = 0;
  const indexScrollList = [];

  const stackFlags = () => {
    if (withRecoil) {
      return Object.keys(stacks).map((flag) => {
        return flag;
      });
    } else {
      return stacks.map((stack) => {
        return stack.flag;
      });
    }
  };

  const isSelected = (staticStack) => {
    return stackFlags().includes(staticStack.flag);
  };

  const handleStackSelection = (searchedStack) => {
    withRecoil
      ? handleRecoilStackClick(searchedStack)
      : handleStackClick(isSelected(searchedStack), searchedStack);
  };

  const handleStackClick = (isSelected, staticStack) => {
    setStacks((selectedStacks) => {
      if (!isSelected) {
        return selectedStacks.concat([
          {
            flag: staticStack.flag,
            name: staticStack.name,
            requiredYear: 0,
          },
        ]);
      } else {
        setStacks(
          selectedStacks.filter((selected) => {
            return selected.flag !== staticStack.flag;
          }),
        );
      }
    });
  };

  const handleRecoilStackClick = (staticStack) => {
    if (!Object.prototype.hasOwnProperty.call(stacks, staticStack.flag)) {
      const newStacks = { ...stacks };
      newStacks[staticStack.flag] = [stackYearMin, stackYearMax];
      setStacks(newStacks);
    } else {
      const newStacks = { ...stacks };
      delete newStacks[staticStack.flag];
      setStacks(newStacks);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.keyCode === 38) {
      // Arrow Up
      e.preventDefault();
      if (highlightedStackIdx === 0) {
        setHighlightedStackIdx(searchedStacks.length - 1);
      } else {
        setHighlightedStackIdx((highlightedStackIdx - 1) % searchedStacks.length);
        elementListRef.current[highlightedStackIdx - 1]?.scrollIntoView({ block: 'nearest' });
      }
    } else if (e.keyCode === 40) {
      // Arrow Down
      e.preventDefault();
      setHighlightedStackIdx((highlightedStackIdx + 1) % searchedStacks.length);
      elementListRef.current[highlightedStackIdx + 1]?.scrollIntoView({ block: 'nearest' });
    } else if (e.keyCode === 13) {
      // Enter
      e.preventDefault();
      handleStackSelection(searchedStacks[highlightedStackIdx]);
    }
  };
  return (
    <section className={cx('content')} style={sx}>
      <InputSearchGroup size={'lg'}>
        <Input
          size={'sm'}
          placeholder="검색할 기술 스택을 입력해 주세요."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
          }}
          onKeyDown={handleInputKeyDown}
        />
      </InputSearchGroup>
      <div style={{ marginTop: '8px' }}>
        {withIndexScroll && (
          <SideBarIndexScroll indexScrollList={indexScrollList} elementListRef={elementListRef} />
        )}
        <ul
          className={cx(['content-body-ul', scrollable ? 'scrollable-y' : ''])}
          style={{ height: `${scrollHeight}px` }}
        >
          {searchedStacks.map((searchedStack, index) => {
            if (withIndexScroll) {
              if (searchedStack.name.toUpperCase().startsWith(indexIdList[curIndex])) {
                indexScrollList.push(index);
                curIndex = curIndex + 1;
              } else if (searchedStack.name.toUpperCase() > indexIdList[curIndex]) {
                indexScrollList.push(null);
                curIndex = curIndex + 1;
              }
            }
            return (
              <li
                key={searchedStack.flag}
                className={cx([
                  'content-body-li',
                  index === highlightedStackIdx ? 'highlighted' : '',
                ])}
                onClick={() => {
                  handleStackSelection(searchedStack);
                }}
                onMouseOver={() => setHighlightedStackIdx(index)}
              >
                {isSelected(searchedStack) && <Check width={16} height={16} fill={'#34C759'} />}
                <span ref={(el) => (elementListRef.current[index] = el)}>{searchedStack.name}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export { StackSearchBar, StackFilterBox };
